import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Action, State, StateContext } from "@ngxs/store";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { RequestState, createRequestAction } from "ngxs-requests-plugin";

import { environment } from "../../../../environments/environment";
import { AppConfigService } from "../../services/app-config.service";
import { FilterService } from "../../services/filter.service";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "../../services/local-storage.service";
import { IFilterFirmCountResult, IRestriction } from "../../interfaces/filter.interface";
import { FilterValueTypeEnum } from "../../interfaces/filter-value-type.enum";
import { IFilterValue } from "../../interfaces/filter-value.interface";
import { DateRestrictionTypes } from "../../interfaces/date-restriction-type.enum";

import { SaveRestriction, GetRestriction, ResetCurrentRestriction, SetCurrentRestrictionDateArgs, SetCurrentRestrictionDateType, SetCurrentRestrictionPosition, SetCurrentRestrictionValueArgs, SetCurrentRestrictionValueType, SetCurrentRestrictionInvert, SetCurrentRestrictionVariable, SetFilter, RemoveRestriction, GetFilterCounts, GetFilterCountsSuccess, GetFilterCountsFail, GetFilterCountsStream, GetFiltersPreset, GetFiltersPresetSuccess, GetFiltersPresetFail, GetFirmIdListPreset, GetFirmIdListPresetFail, GetFirmIdListPresetSuccess, SaveFilterPreset, SaveFilterPresetSuccess, SaveFilterPresetFail, GetFiltersPresetList, GetFiltersPresetListSuccess, GetFiltersPresetListFail, ClearFilter, SetCurrentFilterCountResult, SetFilterCountsSubmittedHash, SaveFirmIdListPreset, SaveFirmIdListPresetSuccess, SaveFirmIdListPresetFail, GetFirmIdListPresetList, GetFirmIdListPresetListSuccess, GetFirmIdListPresetListFail, GetFilterCountsSuccessWithSample, ClearFilterState } from "./filter.actions";
import { ResetSelectedSubtreeVariables } from "../selected-subtree-variables/selected-subtree-variables.actions";
import { ResetVariableSubtree } from "../variables-subtrees/variables-subtrees.actions";

import { statusCodeErrorHandler } from "../../../shared/utils/status-code-error-handler";
import { IPresetReduced } from "../../interfaces/presets.interface";
import { SampleService } from "../../services/sample.service";

export interface FilterStateModel {
  ids: string[];
  entities: {[key: string]: any};
  currentFilter: IRestriction[][];
  currentRestriction: IRestriction;
  activeButNotSubmittedFilterCountResult: number;
  currentFilterCounts: IFilterFirmCountResult[];
  activeButNotSubmittedFilterCountsAreLoading: boolean;
  activeButNotSubmittedFiltersHash: string;
  filterCountsSubmittedHash?: string;
  filtersPresetList: IPresetReduced[];
  filtersPresetName: string;
  filtersPreset: IRestriction[][];

  serverSideEventHashes: string[];
  lastFilterHash?: string;
}

const defaultRestriction = {
  filterVariable: null,
  filterValueType: null,
  filterValue: null,
  dateRestrictionType: DateRestrictionTypes.mostRecentValue,
  invert: false,
  dateRestrictionArgs: null,
  description: null,
}

export interface FirmIdListStateModel {
  firmIdListPresetList: IPresetReduced[];
  firmIdListPresetName: string;
  firmIdListPreset: string[];
}

@RequestState('getFiltersPresetList')
@Injectable()
export class GetFiltersPresetListRequestState {}

@RequestState('getFirmIdListPresetList')
@Injectable()
export class GetFirmIdListPresetListRequestState {}

@RequestState('getFilterPreset')
@Injectable()
export class GetFilterPresetRequestState {}

@RequestState('getFirmIdListPreset')
@Injectable()
export class GetFirmIdListPresetRequestState {}

@RequestState('saveFilterPreset')
@Injectable()
export class SaveFilterPresetRequestState {}

@RequestState('saveFirmIdListPreset')
@Injectable()
export class SaveFirmIdListPresetRequestState {}

@RequestState('getFilterCounts')
@Injectable()
export class GetFilterCountsRequestState {}

@State<FilterStateModel>({
  name: 'filters',
  defaults: {
    ids: [],
    entities: {},
    currentFilter: [],
    currentRestriction: defaultRestriction,
    activeButNotSubmittedFilterCountResult: null,
    currentFilterCounts: [],
    activeButNotSubmittedFilterCountsAreLoading: false,
    activeButNotSubmittedFiltersHash: null,
    filterCountsSubmittedHash: null,
    filtersPresetList: null,
    filtersPresetName: null,
    filtersPreset: null,
    serverSideEventHashes: [],
  }
})
@Injectable()
export class FilterState {
  constructor(
    private http: HttpClient,
    private filterService: FilterService,
    private localStorageService: LocalStorageService,
    private _snackBar: MatSnackBar,
    private translate: TranslateService,
    private injector: Injector,
    private sampleService: SampleService,
    private appConfig: AppConfigService,
  ) {}

  // Helper function to convert arrays to string with proper formatting
  listToString(arr: string[], joinType: string): string {
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} ${joinType} ${arr[1]}`;
    return `${arr.slice(0, -1).join(', ')} ${joinType} ${arr[arr.length - 1]}`;
  }
  

  addRestrictionDescription(restriction: IRestriction) {
    let description = '';
    let prefix = '';

    // Date restriction
    const orAndMapper = (type: DateRestrictionTypes, invert:boolean=false) => {
      if (!invert) {
        return (type === DateRestrictionTypes.inAllPeriods) ? this.translate.instant('SHARED.COMMON.AND').toLowerCase() : this.translate.instant('SHARED.COMMON.OR_LOWER')}
      else {
        return (type === DateRestrictionTypes.inAllPeriods) ? this.translate.instant('SHARED.COMMON.OR_LOWER') : this.translate.instant('SHARED.COMMON.AND').toLowerCase();
      }
    };

    switch(restriction.dateRestrictionType) {
      case(DateRestrictionTypes.mostRecentValue):
        prefix = this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.MOST_RECENT');
        break;
      case(DateRestrictionTypes.ever):
        prefix = restriction.invert ? this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.NOT_EVER') : this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.EVER');
        break;
      case(DateRestrictionTypes.contemporaneous):
        prefix = this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.CONTEMPORANEOUS');
        break;
      case(DateRestrictionTypes.inAllPeriods):
      case(DateRestrictionTypes.inAnyPeriod):
        if (restriction.dateRestrictionArgs) {
          const years = restriction.dateRestrictionArgs.map(String);
          const relevantYearsLabel = `${this.translate.instant('SHARED.COMMON.IN_DATES')} ${this.listToString(years, orAndMapper(restriction.dateRestrictionType, restriction.invert))}`;
          prefix = this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.ANYALL', { yearslabel: relevantYearsLabel });
        }
        break;
    }

    // Filter type
    let criterionLabel = '';
    const useInvertedLabel = (restriction.invert && restriction.dateRestrictionType != DateRestrictionTypes.ever)
    let value;
    switch(restriction.filterValueType) {
      case FilterValueTypeEnum.exactmatch:
        value = Array.isArray(restriction.filterValue) 
          ? this.listToString(restriction.filterValue as string[], this.translate.instant('SHARED.COMMON.OR_LOWER'))
          : restriction.filterValue;
        criterionLabel = useInvertedLabel 
          ? this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.NOT_EQUALS', {variable: restriction.filterVariable.name, value: value})
          : this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.EQUALS', {variable: restriction.filterVariable.name, value: value});
        break;
      case FilterValueTypeEnum.anymatch:
        if (!useInvertedLabel) {
          value = Array.isArray(restriction.filterValue)
            ? this.listToString(restriction.filterValue as string[], this.translate.instant('SHARED.COMMON.OR_LOWER'))
            : restriction.filterValue;
          criterionLabel = this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.CONTAINS', {variable: restriction.filterVariable.name, value: value});
        } else {
          value = Array.isArray(restriction.filterValue)
            ? this.listToString(restriction.filterValue as string[], this.translate.instant('SHARED.COMMON.AND_LOWER'))
            : restriction.filterValue;
          criterionLabel = this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.NOT_CONTAINS', {variable: restriction.filterVariable.name, value: value})
        }          
        break;
        
      case FilterValueTypeEnum.nonmissing:
        criterionLabel = useInvertedLabel 
          ? this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.MISSING', {variable: restriction.filterVariable.name})
          : this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.NOT_MISSING', {variable: restriction.filterVariable.name});
        break;
        
      case FilterValueTypeEnum.minmax:
        const filterValue = restriction.filterValue as IFilterValue;
        if (filterValue.min && !filterValue.max) {
          criterionLabel = useInvertedLabel 
            ? this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.NOT_ATLEAST', {variable: restriction.filterVariable.name, value: filterValue.min})
            : this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.ATLEAST', {variable: restriction.filterVariable.name, value: filterValue.min});
        } else if (filterValue.max && !filterValue.min) {
          criterionLabel = useInvertedLabel 
            ? this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.NOT_ATMOST', {variable: restriction.filterVariable.name, value: filterValue.max})
            : this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.ATMOST', {variable: restriction.filterVariable.name, value: filterValue.max});
        } else if (filterValue.min && filterValue.max) {
          criterionLabel = useInvertedLabel 
            ? this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.NOT_BETWEEN', {variable: restriction.filterVariable.name, min: filterValue.min, max: filterValue.max})
            : this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.OPERATORS.BETWEEN', {variable: restriction.filterVariable.name, min: filterValue.min, max: filterValue.max});
        } else {
          throw new Error('filterValueType MINMAX requires at least one of min or max to be set in filterValue (provided: ' + JSON.stringify(filterValue) + ')');
        }
        break;
        
      default:
        throw new Error(`Unknown filterValueType: ${restriction.filterValueType}`);
    }

    // Special case for founding year
    if (restriction.filterVariable.tag === 'FoundingYear') {
      prefix = prefix.replace(this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.MOST_RECENT'), this.translate.instant('MULTIPLE_FIRMS.FILTER_CRITERIA.RESTRICTIONS.LABELS.PREFIX.TIMELESS'));
    }

    return `${prefix} ${criterionLabel}`;
  }

  @Action(ClearFilterState)
  clearFilterState(
    { patchState }: StateContext<FilterStateModel>,
  ) {
    patchState({
      currentFilter: null,
      currentRestriction: null,
      activeButNotSubmittedFilterCountResult: null,
      currentFilterCounts: null,
      activeButNotSubmittedFilterCountsAreLoading: false,
      activeButNotSubmittedFiltersHash: null,
      filterCountsSubmittedHash: null
    });
  }

  @Action(GetFiltersPresetList)
  getFiltersPresetList(
    { dispatch }: StateContext<FilterStateModel>,
  ) {
    const params = new HttpParams().set('dbName', this.appConfig.dbName);
    const request = this.http.get(environment.api + 'user/presets/filters/list', {params});

    return dispatch(
      createRequestAction({
        request,
        state: GetFiltersPresetListRequestState,
        successAction: GetFiltersPresetListSuccess,
        failAction: GetFiltersPresetListFail,
      })
    );
  }

  @Action(GetFiltersPresetListSuccess)
  getFiltersPresetListSuccess(
    { patchState }: StateContext<FilterStateModel>,
    { payload: response }: GetFiltersPresetListSuccess
  ) {
    const filtersLists = response.content.map(item => ({
      ...item,
      last_accessed_at: new Date(item.last_accessed_at)
    }));
    patchState({filtersPresetList: filtersLists});
  }

  @Action(GetFiltersPreset)
  getFilterPreset(
    { dispatch, patchState }: StateContext<FilterStateModel>,
    { payload: name }: GetFiltersPreset
  ) {
    const params = new HttpParams().set('dbName', this.appConfig.dbName).set('name', name);
    const request = this.http.get(environment.api + 'user/presets/filters/get', {params});
    patchState({filtersPresetName: name, filtersPreset: null});

    return dispatch(
      createRequestAction({
        request,
        state: GetFilterPresetRequestState,
        successAction: GetFiltersPresetSuccess,
        failAction: GetFiltersPresetFail
      })
    );
  }

  @Action(GetFiltersPresetSuccess)
  getFilterPresetSuccess(
    { patchState }: StateContext<FilterStateModel>,
    { payload: response }: GetFiltersPresetSuccess
  ) {
    patchState({filtersPreset: response.content});
  }

  @Action(SaveFilterPreset)
  saveFilterPreset(
    { dispatch, getState }: StateContext<FilterStateModel>,
    { name: name, filters: filters}: SaveFilterPreset
  ) {
    filters = filters.map(filter => {
      return filter.reduce((acc, cur) => {
        const filterVariable = cur.filterVariable.tag;
        const {filterValueType, dateRestrictionType} = cur;
        let filter = {filterVariable, filterValueType, dateRestrictionType};

        if (cur.filterValueType === FilterValueTypeEnum.minmax) {
          filter['filterValue'] = {min: cur.filterValue.min, max: cur.filterValue.max};
        } else if ((cur.filterValueType === FilterValueTypeEnum.exactmatch) || (cur.filterValueType === FilterValueTypeEnum.anymatch)) {
          filter['filterValue'] = cur.filterValue;
        } else {
          filter['filterValue'] = null;
        }
        if (cur.dateRestrictionType === DateRestrictionTypes.inAllPeriods || cur.dateRestrictionType === DateRestrictionTypes.inAnyPeriod || cur.dateRestrictionType === DateRestrictionTypes.contemporaneous) {
          filter['dateRestrictionArgs'] = cur.dateRestrictionArgs;
        }

        return [...acc, filter];
      }, []);
    })

    const body = {
      dbName: this.appConfig.dbName,
      dbVersion: "latest",
      name: name,
      filters: filters
    }
    const request = this.http.post(environment.api + 'user/presets/filters/post', body);

    return dispatch(
      createRequestAction({
        request,
        state: SaveFilterPresetRequestState,
        successAction: SaveFilterPresetSuccess,
        failAction: SaveFilterPresetFail
      })
    );
  }

  @Action(SaveFilterPresetSuccess)
  saveFilterPresetSuccess(
    ctx: StateContext<FilterStateModel>,
    { payload: response }: SaveFilterPresetSuccess
  ) {
  }

  @Action(SaveRestriction)
  saveRestriction(
    { getState, patchState, dispatch }: StateContext<FilterStateModel>,
    { payload: editMode }: SaveRestriction
  ) {
    let currentFilter = [...getState().currentFilter];
    let restriction = getState().currentRestriction;
    let description = this.addRestrictionDescription(restriction);
    
    restriction = {...restriction, description, position: restriction?.position ? restriction.position : {i: 0, j: 0}};
    const {i, j} = restriction?.position;
    
    if (editMode) {
      let current = [...currentFilter[i]];
      current.splice(j, 1, restriction);
      currentFilter.splice(i, 1, current);
    } else if (currentFilter[i]) {
      const current = [...currentFilter[i], restriction];
      currentFilter.splice(i, 1, current);
    } else {
      currentFilter.splice(i, 1, [restriction]);
    }

    patchState({currentFilter});
    dispatch([new GetFilterCounts(), new ResetCurrentRestriction(), new ResetSelectedSubtreeVariables(), new ResetVariableSubtree()]);
  }

  @Action(GetRestriction)
  getRestriction(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: {i, j} }: GetRestriction
  ) {
    const currentFilter = getState().currentFilter;
    const currentRestriction = currentFilter[i][j];

    patchState({currentRestriction: {...currentRestriction, position: {i, j}}});
  }

  @Action(RemoveRestriction)
  removeRestriction(
    { dispatch, getState, patchState }: StateContext<FilterStateModel>,
    { payload: {i, j} }: RemoveRestriction
  ) {
    const currentFilter = getState().currentFilter;
    const updatedFilter = currentFilter.reduce((acc: IRestriction[][], cur: IRestriction[], index) => {
      if (index !== i) {
        return [...acc, cur];
      } else {
        let current = [...cur];
        current.splice(j, 1);
        return !!current.length ? [...acc, current] : [...acc];
      }
    }, []);

    patchState({currentFilter: updatedFilter});
    dispatch(new GetFilterCounts());
  }
  
  @Action(SetCurrentRestrictionPosition)
  setCurrentRestrictionPosition(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: {i, j}}: SetCurrentRestrictionPosition
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, position: {i, j}};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(SetCurrentRestrictionVariable)
  setCurrentRestrictionVariable(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload }: SetCurrentRestrictionVariable
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, filterVariable: payload.filterVariable, treeIndex: payload.treeIndex, branchIndex: payload.branchIndex};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(SetCurrentRestrictionValueType)
  setCurrentRestrictionValueType(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: filterValueType}: SetCurrentRestrictionValueType
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, filterValueType};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(SetCurrentRestrictionValueArgs)
  setCurrentRestrictionValueArgs(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: filterValue}: SetCurrentRestrictionValueArgs
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, filterValue};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(SetCurrentRestrictionDateType)
  setCurrentRestrictionDateType(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: dateRestrictionType}: SetCurrentRestrictionDateType
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, dateRestrictionType};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(SetCurrentRestrictionDateArgs)
  setCurrentRestrictionDateArgs(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: dateRestrictionArgs}: SetCurrentRestrictionDateArgs
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, dateRestrictionArgs};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(SetCurrentRestrictionInvert)
  setCurrentRestrictionInvert(
    { getState, patchState }: StateContext<FilterStateModel>,
    { payload: invert }: SetCurrentRestrictionInvert
  ) {
    const restriction = getState().currentRestriction;
    const updatedRestriction = {...restriction, invert};

    patchState({currentRestriction: updatedRestriction});
  }

  @Action(ResetCurrentRestriction)
  resetCurrentRestriction(
    { patchState, getState }: StateContext<FilterStateModel>,
  ) {
    const currentRestriction = getState().currentRestriction;
    patchState({currentRestriction: {...currentRestriction, ...defaultRestriction}});
  }

  @Action(SetFilter)
  setFilter(
    { patchState, dispatch }: StateContext<FilterStateModel>,
    { payload: filters }: SetFilter
  ) {
    const newFilter = filters.map(filter => {
      return filter.map(restriction => {
        const description = this.addRestrictionDescription(restriction);

        return {...restriction, description};
      })
    })

    patchState({currentFilter: newFilter});
    dispatch(new GetFilterCounts(null, true));
  }

  @Action(ClearFilter)
  resetFilter(
    { patchState }: StateContext<FilterStateModel>
  ) {
    patchState({currentFilter: []});
  }

  @Action(GetFilterCounts)
  getFilterCounts(
    { dispatch, getState, patchState }: StateContext<FilterStateModel>,
    { payload: filters, setSample = false }: GetFilterCounts
  ) {
    if (!filters) {
      filters = getState().currentFilter.map(filter => {
        return filter.reduce((acc, cur) => {
          const filterVariable = cur.filterVariable.tag;
          const {filterValueType, dateRestrictionType} = cur;
          let filter = {filterVariable, filterValueType, dateRestrictionType};
  
          if (cur.filterValueType === FilterValueTypeEnum.minmax) {
            filter['filterValue'] = {min: cur.filterValue.min, max: cur.filterValue.max};
          } else if ((cur.filterValueType === FilterValueTypeEnum.exactmatch) || (cur.filterValueType === FilterValueTypeEnum.anymatch)) {
            filter['filterValue'] = cur.filterValue;
          } else {
            filter['filterValue'] = null;
          }
          if (cur.dateRestrictionType === DateRestrictionTypes.inAllPeriods || cur.dateRestrictionType === DateRestrictionTypes.inAnyPeriod || cur.dateRestrictionType === DateRestrictionTypes.contemporaneous) {
            filter['dateRestrictionArgs'] = cur.dateRestrictionArgs;
          }
          filter['invert'] = cur.invert;
  
          return [...acc, filter];
        }, []);
      })
    }
    
    const body = {
      dbName: this.appConfig.dbName,
      filters
    }

    const request = this.http.post(environment.api + 'db/counts', body);

    return dispatch(
      createRequestAction({
        request,
        state: GetFilterCountsRequestState,
        successAction: setSample ? GetFilterCountsSuccessWithSample : GetFilterCountsSuccess,
        failAction: GetFilterCountsFail,
      })
    );
  }
  @Action(GetFilterCountsSuccessWithSample)
  getFilterCountsSuccessWithSample(
    { dispatch, patchState, getState }: StateContext<FilterStateModel>,
    { payload: response }: GetFilterCountsSuccess
  ) {


    if (Array.isArray(response?.content) && !!response?.content?.length) {
      const filterCountResult = response.content[response.content.length - 1]?.count_firms;
      this.sampleService.setSample(response.hash, "filters", filterCountResult)
      patchState({activeButNotSubmittedFilterCountResult: filterCountResult, activeButNotSubmittedFilterCountsAreLoading: false, currentFilterCounts: response.content, activeButNotSubmittedFiltersHash: response.hash});
      this.sampleService.storeSampleCount({hash: response.hash, type: 'filters', count: filterCountResult});
    } else {
      this.sampleService.setSample(response.hash, "filters", null)
      patchState({activeButNotSubmittedFilterCountsAreLoading: true, activeButNotSubmittedFiltersHash: response.hash, activeButNotSubmittedFilterCountResult: null});
      dispatch(new GetFilterCountsStream(response.hash));
    }
    
    this.localStorageService.setItem('lastFiltersHash', response.hash);
  }

  @Action(GetFilterCountsSuccess)
  getFilterCountsSuccess(
    { dispatch, patchState, getState }: StateContext<FilterStateModel>,
    { payload: response }: GetFilterCountsSuccess
  ) {


    if (Array.isArray(response?.content) && !!response?.content?.length) {
      const filterCountResult = response.content[response.content.length - 1]?.count_firms;
      patchState({activeButNotSubmittedFilterCountResult: filterCountResult, activeButNotSubmittedFilterCountsAreLoading: false, currentFilterCounts: response.content, activeButNotSubmittedFiltersHash: response.hash});
      this.sampleService.storeSampleCount({hash: response.hash, type: 'filters', count: filterCountResult});
    } else {
      patchState({activeButNotSubmittedFilterCountsAreLoading: true, activeButNotSubmittedFiltersHash: response.hash, activeButNotSubmittedFilterCountResult: null});
      dispatch(new GetFilterCountsStream(response.hash));
    }
    
    this.localStorageService.setItem('lastFiltersHash', response.hash);
  }

  @Action(GetFilterCountsFail)
  getVariablesTreeFail(
    { dispatch }: StateContext<FilterStateModel>,
    { payload: error }: GetFilterCountsFail
  ) {
    statusCodeErrorHandler(error, dispatch)
  }

  @Action(GetFilterCountsStream)
  getFilterCountsStream(
    { dispatch, getState, patchState }: StateContext<FilterStateModel>,
    { payload: hash }: GetFilterCountsStream
  ) {
    const token = this.localStorageService.getItem('sequenza_token');
    
    const ctrl = new AbortController();
    fetchEventSource(environment.api + 'db/counts/stream/' + hash, {
      headers: {
        "Authorization": `Bearer ${token}`
      },
      signal: ctrl.signal,
      onmessage: (e) => {
        if (e.event === "finished") {
          const response = JSON.parse(e.data);

          if (!!response) {
            const filterCountResult = response[response.length - 1]?.count_firms;
            const activeButNotSubmittedHash = getState().activeButNotSubmittedFiltersHash;

            if (hash === activeButNotSubmittedHash) {
              patchState({currentFilterCounts: response, activeButNotSubmittedFilterCountsAreLoading: false, activeButNotSubmittedFilterCountResult: filterCountResult, activeButNotSubmittedFiltersHash: null});
              this.localStorageService.setItem('lastFiltersHash', hash);
            } 
            else if (activeButNotSubmittedHash === null) {console.debug(`External request for hash ${hash.slice(0, 6)}, not patching filter criteria state.`)}       
            else {console.debug(`Hash mismatch, ignoring event. Event relates to ${hash.slice(0, 6)} whereas the latest request is ${activeButNotSubmittedHash.slice(0, 8)}`);}

            patchState({serverSideEventHashes: getState().serverSideEventHashes.filter(e => e !== hash)});
            this.sampleService.storeSampleCount({hash, type: 'filters', count: filterCountResult});
            ctrl.abort();
          }
        } else if (e.event === "invalid" || e.event === "failed" || e.event === "error") {
          const activeButNotSubmittedHash = getState().activeButNotSubmittedFiltersHash;
          patchState({activeButNotSubmittedFilterCountsAreLoading: false});
          if (hash === activeButNotSubmittedHash) {
            patchState({activeButNotSubmittedFiltersHash: null});
          }

          patchState({serverSideEventHashes: getState().serverSideEventHashes.filter(e => e !== hash)});
          ctrl.abort();
        }
      },
      onerror: (err) => {
        if (err?.message?.includes('Reconnecting')) {
          ctrl.abort();
          dispatch(new GetFilterCountsStream(hash));
        } else {
          const activeButNotSubmittedHash = getState().activeButNotSubmittedFiltersHash;
          if (hash === activeButNotSubmittedHash) {
            patchState({activeButNotSubmittedFiltersHash: null});
            patchState({activeButNotSubmittedFilterCountsAreLoading: false});
          }

          patchState({serverSideEventHashes: getState().serverSideEventHashes.filter(e => e !== hash)});
          ctrl.abort();
        }
        throw err;
      }
    });

    patchState({serverSideEventHashes: [...getState().serverSideEventHashes, hash]})
  }

  @Action(SetCurrentFilterCountResult)
  setFilterCountResult(
    { patchState }: StateContext<FilterStateModel>,
    { payload: activeButNotSubmittedFilterCountResult }: SetCurrentFilterCountResult
  ) {
    patchState({activeButNotSubmittedFilterCountResult});
    this.localStorageService.filterCountResultSubject$.next(activeButNotSubmittedFilterCountResult);
  }

  @Action(SetFilterCountsSubmittedHash)
  setFilterCountsSubmittedHash({ patchState, getState }: StateContext<FilterStateModel>,) {
    const activeButNotSubmittedFiltersHash = getState().activeButNotSubmittedFiltersHash;
    patchState({filterCountsSubmittedHash: activeButNotSubmittedFiltersHash});
  }
}

@State<FirmIdListStateModel>({
  name: 'firmIdList',
  defaults: {
    firmIdListPresetList: null,
    firmIdListPresetName: null,
    firmIdListPreset: null,
  }
})
@Injectable()
export class FirmIdListState {
  constructor(
    private http: HttpClient,
    private filterService: FilterService,
    private localStorageService: LocalStorageService,
    private _snackBar: MatSnackBar,
    private translate: TranslateService,
    private injector: Injector,
    private appConfig: AppConfigService,
  ) {}

  @Action(SaveFirmIdListPreset)
  saveFirmIdListPreset(
    { dispatch }: StateContext<FirmIdListStateModel>,
    { name: name, firmIdList: firmIdList}: SaveFirmIdListPreset
  ) {
    const body = {
      dbName: this.appConfig.dbName,
      dbVersion: "latest",
      name: name,
      firmIdList: firmIdList
    }
    const request = this.http.post(environment.api + 'user/presets/firmIdList/post', body);

    return dispatch(
      createRequestAction({
        request,
        state: SaveFirmIdListPresetRequestState,
        successAction: SaveFirmIdListPresetSuccess,
        failAction: SaveFirmIdListPresetFail
      })
    );
  }

  @Action(GetFirmIdListPresetList)
  getFirmIdListPresetList(
    { dispatch }: StateContext<FirmIdListStateModel>,
  ) {
    const params = new HttpParams().set('dbName', this.appConfig.dbName);
    const request = this.http.get(environment.api + 'user/presets/firmIdList/list', {params});

    return dispatch(
      createRequestAction({
        request,
        state: GetFirmIdListPresetListRequestState,
        successAction: GetFirmIdListPresetListSuccess,
        failAction: GetFirmIdListPresetListFail,
      })
    );
  }
  
  @Action(GetFirmIdListPresetListSuccess)
  getFirmIdListPresetListSuccess(
    { patchState }: StateContext<FirmIdListStateModel>,
    { payload: response }: GetFiltersPresetListSuccess
  ) {
    const presetList = response.content.map(item => ({
      ...item,
      last_accessed_at: new Date(item.last_accessed_at)
    }));
    patchState({firmIdListPresetList: presetList});
  }

  @Action(GetFirmIdListPreset)
  getFirmIdListPreset(
    { dispatch, patchState }: StateContext<FirmIdListStateModel>,
    { payload: name }: GetFirmIdListPreset
  ) {
    const params = new HttpParams().set('dbName', this.appConfig.dbName).set('name', name);
    const request = this.http.get(environment.api + 'user/presets/firmIdList/get', {params});
    patchState({firmIdListPresetName: name, firmIdListPreset: null});

    return dispatch(
      createRequestAction({
        request,
        state: GetFirmIdListPresetRequestState,
        successAction: GetFirmIdListPresetSuccess,
        failAction: GetFirmIdListPresetFail
      })
    );
  }

  @Action(GetFirmIdListPresetSuccess)
  getFirmIdListPresetSuccess(
    { patchState }: StateContext<FirmIdListStateModel>,
    { payload: response }: GetFirmIdListPresetSuccess
  ) {
    patchState({firmIdListPreset: response.content});
  }

}